// src/ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  // Pages that should ALWAYS be in light mode
  const lightModeOnlyPages = ['/', '/pricing', '/privacy', '/terms', '/contact-support', '/help-resources'];
  
  const isLightModeOnly = lightModeOnlyPages.includes(location.pathname);

  useEffect(() => {
    // Force light mode for specific pages, otherwise use selected theme
    const appliedTheme = isLightModeOnly ? 'light' : theme;
    document.documentElement.setAttribute('data-theme', appliedTheme);
    
    // Only save theme if not on a light-mode-only page
    if (!isLightModeOnly) {
      localStorage.setItem('theme', theme);
    }
  }, [theme, location.pathname, isLightModeOnly]);

  const toggleTheme = () => {
    // Prevent theme toggle on light-mode-only pages
    if (isLightModeOnly) return;
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme: isLightModeOnly ? 'light' : theme, toggleTheme, isLightModeOnly }}>
      {children}
    </ThemeContext.Provider>
  );
};